import {
  PRINTABLE_PUZZLE_ID,
  PRINTABLE_SHEET_5,
  PRINTABLE_SHEET_10,
  PRINTABLE_SHEET_15,
  PRINTABLE_SHEET_KIDS,
  type PrintableSheet,
} from "./printable";
import { SITE_EMAIL } from "./site";

export type SeoSection = {
  heading: string;
  paragraphs: string[];
};

/** Answers a real player would ask before or during a puzzle. They double as the
    FAQPage structured data, so every answer has to stand on its own — no "see
    above", no sentence that only makes sense next to the grid. */
export type SeoFaq = {
  question: string;
  answer: string;
};

export type SeoLink = {
  href: string;
  label: string;
  blurb: string;
};

export type SeoPage = {
  path: string;
  canonicalPath?: string;
  title: string;
  description: string;
  eyebrow: string;
  heading: string;
  lede: string;
  sections: SeoSection[];
  faqs?: SeoFaq[];
  /** Absolute path of this page's social card. Defaults to the site-wide one. */
  ogImage?: string;
  ogImageAlt?: string;
  /** Paths from LINK_CARDS. A page never links to itself; four is the most that
      still reads as a recommendation rather than a sitemap dump. */
  relatedPaths?: string[];
  /** UTC dates listed as crawlable links on the archive landing page. */
  archiveDates?: string[];
  /** When set, the static document includes these puzzles' empty sheets and answer key. */
  printableSheet?: PrintableSheet;
};

/** One place to write each destination's name and one-line reason. Anchor text and
    the sentence around it are what a link is worth, so they are written once and
    reused rather than retyped as a bare list per page. */
export const LINK_CARDS: Record<string, SeoLink> = {
  "/": {
    href: "/",
    label: "Today's daily nonogram",
    blurb: "One new grid every UTC day, the same puzzle for everyone.",
  },
  "/nonogram": {
    href: "/nonogram",
    label: "Medium nonogram · 10×10",
    blurb: "The balanced size — long enough to reason through, short enough for a break.",
  },
  "/nonogram/easy": {
    href: "/nonogram/easy",
    label: "Easy nonogram · 5×5",
    blurb: "A two-minute grid, and the best place to learn the logic.",
  },
  "/nonogram/hard": {
    href: "/nonogram/hard",
    label: "Hard nonogram · 15×15",
    blurb: "Longer clue sequences and deeper chains of deduction.",
  },
  "/nonogram/20x20": {
    href: "/nonogram/20x20",
    label: "Grand nonogram · 20×20",
    blurb: "The long sitting — a picture with outlines and interiors.",
  },
  "/nonogram/25x25": {
    href: "/nonogram/25x25",
    label: "Epic nonogram · 25×25",
    blurb: "A whole evening in one grid, with a corner map to find your way.",
  },
  "/how-to-play-nonogram": {
    href: "/how-to-play-nonogram",
    label: "How to play",
    blurb: "An interactive 5×5 lesson: clues, crosses, and reading two directions at once.",
  },
  "/nonogram/techniques": {
    href: "/nonogram/techniques",
    label: "Solving techniques",
    blurb: "Slack, overlap, edges and forcing — the four things a single line will tell you.",
  },
  "/nonogram/techniques/overlap": {
    href: "/nonogram/techniques/overlap",
    label: "The overlap technique",
    blurb: "Why a long clue fills squares before you know where its run sits.",
  },
  "/nonogram/techniques/edge": {
    href: "/nonogram/techniques/edge",
    label: "Edge logic",
    blurb: "Why one square settled at the end of a line usually settles several more.",
  },
  "/nonogram/techniques/forcing": {
    href: "/nonogram/techniques/forcing",
    label: "Forcing",
    blurb: "Work out which clue a filled square belongs to, and the run is pinned.",
  },
  "/nonogram/techniques/gaps": {
    href: "/nonogram/techniques/gaps",
    label: "Gap analysis",
    blurb: "Measure the pieces a cross leaves behind, and cross out the ones nothing fits in.",
  },
  "/daily-nonogram/archive": {
    href: "/daily-nonogram/archive",
    label: "Daily archive",
    blurb: "The last 30 daily grids, each date always the same puzzle.",
  },
  "/collection": {
    href: "/collection",
    label: "Your collection",
    blurb: "Every picture you have revealed, kept on this device.",
  },
  "/printable-nonograms/5x5": {
    href: "/printable-nonograms/5x5",
    label: "Printable 5×5 · four to a sheet",
    blurb: "Four beginner grids on one page, answers on the sheet behind.",
  },
  "/printable-nonograms/10x10": {
    href: "/printable-nonograms/10x10",
    label: "Printable 10×10",
    blurb: "Two grids, one per page, at a size worth writing in.",
  },
  "/printable-nonograms/15x15": {
    href: "/printable-nonograms/15x15",
    label: "Printable 15×15",
    blurb: "Two hard grids for an hour on paper, with the key on the last sheet.",
  },
  "/printable-nonograms/for-kids": {
    href: "/printable-nonograms/for-kids",
    label: "Printable nonograms for kids",
    blurb: "Eight 5×5 grids to hand out, with pictures a child will recognise.",
  },
  "/printable-nonograms": {
    href: "/printable-nonograms",
    label: "Printable nonogram",
    blurb: "A 5×5 puzzle and its answer key, sized for A4 or Letter paper.",
  },
};

/** Every page the Printable component serves. It renders whichever sheet the page
    publishes, so adding one is a page definition and a line in the router, not a
    second copy of the component. */
export const PRINTABLE_PATHS = new Set([
  "/printable-nonograms",
  "/printable-nonograms/5x5",
  "/printable-nonograms/10x10",
  "/printable-nonograms/15x15",
  "/printable-nonograms/for-kids",
]);

export function relatedLinks(page: SeoPage): SeoLink[] {
  const paths = page.relatedPaths ?? ["/", "/nonogram/easy", "/nonogram", "/nonogram/hard"];
  return paths
    .filter((path) => path !== page.path && path !== page.canonicalPath)
    .map((path) => LINK_CARDS[path])
    .filter((link): link is SeoLink => link !== undefined)
    .slice(0, 4);
}

export const SEO_PAGES: SeoPage[] = [
  {
    path: "/",
    ogImage: "/og/daily.png",
    ogImageAlt: "Nonogram Now — today's daily nonogram grid",
    title: "Daily Nonogram — A clean logic puzzle every day",
    description: "Play today's clean daily nonogram puzzle online. No signup, no distractions, and your progress stays on this device.",
    eyebrow: "A quiet daily ritual",
    heading: "Today’s Daily Nonogram",
    lede: "Fill the right squares, reveal a tiny picture, and come back tomorrow. No signup and no distractions.",
    faqs: [
      { question: "What is a nonogram?", answer: "A nonogram is a picture logic puzzle. The numbers beside each row and column tell you the lengths of the filled groups in that line, in order, with at least one empty square between groups. Work out where the groups must go and a small picture appears." },
      { question: "Is the daily puzzle the same for everyone?", answer: "Yes. Every player opening the site on the same UTC date gets the same grid, so a time or a streak means the same thing for everyone. The date rolls over at midnight UTC." },
      { question: "Do I need an account to play?", answer: "No. There is no sign-up, no email, and no login. Your unfinished grid, streak, and best times are stored in your own browser, which also means clearing site data clears them." },
      { question: "What happens when I mark a square wrongly?", answer: "It depends on the mode. Challenge flags a wrong square the moment you place it. Relaxed says nothing until you press Check, which is the only way to sit with a wrong guess long enough to reason your way out of it." },
    ],
    relatedPaths: ["/nonogram/easy", "/nonogram", "/nonogram/hard", "/how-to-play-nonogram"],
    sections: [
      {
        heading: "One carefully checked puzzle every day",
        paragraphs: [
          "The daily grid is the same for every player on a given UTC date. Sundays are a quick 5×5, Saturdays a longer 15×15, and weekdays a balanced 10×10. Each puzzle is checked for a single solution before it is published, so careful deduction is always enough.",
        ],
      },
      {
        heading: "How the streak and the share line work",
        paragraphs: [
          "Finish today's grid before midnight UTC to extend a local streak of consecutive days. The count lives in this browser; another device starts at zero. After you finish, the share text is the date, the size, your time and your mistake count — never the picture's name and never the filled grid, so a friend can still play the same day.",
        ],
      },
      {
        heading: "Play without an account",
        paragraphs: [
          "Your unfinished grid, streak, and best times stay in this browser. Start on a phone or desktop, take a break, and continue later on the same device. There is no sign-up, no advertising profile, and nothing to upload.",
        ],
      },
    ],
  },
  {
    path: "/daily-nonogram",
    // Same board as the root. The root is what people link to and share, so it holds
    // the signals and this path consolidates into it rather than competing with it.
    canonicalPath: "/",
    ogImage: "/og/daily.png",
    ogImageAlt: "Nonogram Now — today's daily nonogram grid",
    title: "Daily Nonogram Puzzle — Play today’s free grid",
    description: "Solve today's free Daily Nonogram in your browser, keep a local streak, and share a spoiler-free result.",
    eyebrow: "One new grid every day",
    heading: "Today’s Daily Nonogram",
    lede: "A uniquely solvable picture logic puzzle, ready to play without an account.",
    faqs: [
      { question: "When does the daily nonogram change?", answer: "At midnight UTC. Until then every visitor sees the same grid, and the previous 30 days stay open in the archive." },
      { question: "What size is the daily puzzle?", answer: "It follows the day of the week: Sundays are a quick 5×5, Saturdays a longer 15×15, and the rest of the week a balanced 10×10. Each archived date keeps its original size." },
      { question: "Can I keep a streak without signing up?", answer: "Yes. The streak counts consecutive days you have completed and lives in this browser's storage. Nothing is uploaded, so a different browser or device starts its own count." },
      { question: "Can I share my result without spoiling the picture?", answer: "Yes. The share text carries the date, the grid size, your time and your mistake count — never the answer or the picture's name." },
    ],
    relatedPaths: ["/daily-nonogram/archive", "/nonogram", "/how-to-play-nonogram", "/collection"],
    sections: [
      {
        heading: "How the daily puzzle works",
        paragraphs: [
          "Use the clues beside each row and column to identify groups of filled squares. Mark known empty cells with crosses, combine information in both directions, and reveal the hidden pattern. The date rolls over at midnight UTC; until then every visitor is on the same grid.",
        ],
      },
      {
        heading: "What size today is",
        paragraphs: [
          "The size follows the weekday: Sunday is a 5×5 you can finish in a few minutes, Saturday a 15×15 for a longer sitting, and Monday through Friday a 10×10. Opening an archived date always restores that day's original size, not today's.",
        ],
      },
      {
        heading: "Build a private local streak",
        paragraphs: [
          "Complete today's grid to extend your streak and create a spoiler-free result to share. Progress and statistics are stored locally, with no registration or advertising profile.",
        ],
      },
    ],
  },
  {
    path: "/nonogram",
    ogImage: "/og/medium.png",
    ogImageAlt: "Nonogram Now — a partly solved 10 by 10 nonogram",
    title: "Play Nonogram Online — Free picture logic puzzles",
    description: "Play free nonogram puzzles online with clean controls, automatic saving, and easy, medium, and hard grids.",
    eyebrow: "Play at your pace",
    heading: "Play Nonogram online",
    lede: "Choose a grid, use the number clues, and reveal the hidden pixel picture.",
    faqs: [
      { question: "What grid sizes can I play?", answer: "Three: a 5×5 easy grid, a 10×10 medium grid, and a 15×15 hard grid. You can switch between them at any time without losing an unfinished puzzle in another size." },
      { question: "Do the puzzles always have exactly one solution?", answer: "Yes. Every puzzle is run through a solver before it is published and is only kept if it can be reached by deduction alone, so guessing is never required." },
      { question: "Does my progress save if I close the tab?", answer: "Yes. The grid, the clock and the undo history are written to local browser storage as you play, so a refresh or an accidental back gesture does not cost you the puzzle." },
      { question: "Is it really free?", answer: "Yes — free to play, with no account, no advertising profile, and no paid tier." },
    ],
    relatedPaths: ["/nonogram/easy", "/nonogram/hard", "/", "/how-to-play-nonogram"],
    sections: [
      {
        heading: "Free online Nonogram puzzles",
        paragraphs: [
          "Start with a 5×5 beginner grid, settle into a balanced 10×10 puzzle, or choose a longer 15×15 challenge. Every published puzzle has been verified to have exactly one solution, so guessing is never required.",
        ],
      },
      {
        heading: "Which size to play",
        paragraphs: [
          "5×5 is two minutes and the whole line is visible at a glance. 10×10 is the everyday size: several groups per line, still one sitting. 15×15 is a longer chain of small deductions; on a phone the squares stay large enough to tap and the board pans instead of shrinking. You can leave a puzzle in one size and open another without losing the unfinished grid.",
        ],
      },
      {
        heading: "Designed for touch, mouse, and keyboard",
        paragraphs: [
          "Fill cells, place crosses, erase marks, drag across a line, or undo a move. The current puzzle saves automatically in your browser so an accidental refresh does not erase your work.",
        ],
      },
    ],
  },
  {
    path: "/nonogram/easy",
    ogImage: "/og/easy.png",
    ogImageAlt: "Nonogram Now — a 5 by 5 beginner nonogram",
    title: "Easy Nonogram Puzzles — Free 5×5 grids",
    description: "Start with a free easy 5×5 nonogram puzzle. Clear clues and a compact grid make it ideal for beginners.",
    eyebrow: "5×5 · Easy",
    heading: "Easy Nonogram puzzle",
    lede: "A small, friendly grid for learning the logic or taking a two-minute break.",
    faqs: [
      { question: "How long does a 5×5 nonogram take?", answer: "Usually one to three minutes once you know the rules. It is short enough to finish in a queue and small enough to hold the whole grid in your head." },
      { question: "Is 5×5 a good size to start with?", answer: "It is the best size to start with. Five clues per line means every deduction is visible without counting far ahead, so the logic is easy to see rather than easy to guess." },
      { question: "How do I begin an easy puzzle?", answer: "Start with the fullest lines. In a 5×5, a clue of 5 fills the line outright, and a clue of 4 always fills the middle three squares whichever way it slides. Those certain squares then constrain the crossing lines." },
      { question: "Do I ever need to guess?", answer: "No. Easy puzzles are verified to have a single solution reachable by deduction, so if a square is unclear there is always another line that settles it first." },
    ],
    relatedPaths: ["/how-to-play-nonogram", "/nonogram", "/nonogram/hard", "/"],
    sections: [
      {
        heading: "A simple place to learn Nonograms",
        paragraphs: [
          "Easy puzzles use a compact 5×5 grid, so every clue is quick to compare. Begin with rows or columns whose filled groups nearly cover the available space, then mark confirmed empty cells.",
        ],
      },
      {
        heading: "Opening moves on a 5×5",
        paragraphs: [
          "A clue of 5 fills the whole line. A clue of 4 always covers the middle three squares: the block can sit one cell left or one cell right, and those three overlap in every placement. Fill them, then look at the columns they punch through. A 3 on a five-wide line covers at least the centre square. Once those certain squares are in, the crossing clues usually finish the grid.",
        ],
      },
      {
        heading: "Practice without pressure",
        paragraphs: [
          "There is no account and no leaderboard. Use Undo whenever a mark feels uncertain, and your unfinished puzzle will remain available on this device.",
        ],
      },
    ],
  },
  {
    path: "/nonogram/medium",
    canonicalPath: "/nonogram",
    ogImage: "/og/medium.png",
    ogImageAlt: "Nonogram Now — a partly solved 10 by 10 nonogram",
    title: "Medium Nonogram Puzzles — Free 10×10 grids",
    description: "Play a free medium 10×10 nonogram puzzle with touch-friendly controls and automatic progress saving.",
    eyebrow: "10×10 · Medium",
    heading: "Medium Nonogram puzzle",
    lede: "A balanced picture puzzle with enough clues to reward careful deduction.",
    faqs: [
      { question: "How long does a 10×10 nonogram take?", answer: "Around five to fifteen minutes depending on how much you cross out. It is the size most people settle on for a daily habit." },
      { question: "What makes 10×10 harder than 5×5?", answer: "Lines carry several groups, so a clue rarely resolves on its own. Progress comes from overlapping what a line must contain with what the crossing lines have already ruled out." },
      { question: "Can I pause and come back?", answer: "Yes. The grid, elapsed time and undo history save automatically in this browser, so you can close the page and continue the same puzzle later on the same device." },
    ],
    relatedPaths: ["/nonogram/easy", "/nonogram/hard", "/", "/how-to-play-nonogram"],
    sections: [
      {
        heading: "A balanced 10×10 challenge",
        paragraphs: [
          "Medium Nonograms introduce longer clue combinations while keeping the full board manageable on a phone. Work from the most constrained lines and revisit crossing clues after every confirmed group.",
        ],
      },
      {
        heading: "Why 10×10 needs both directions",
        paragraphs: [
          "A ten-wide line with clues 3 2 still has slack, so you rarely fill a square from that line alone. Cross the squares the groups cannot reach, then wait for a crossing clue of 6 or 7 to punch through. The 10×10 is where that two-way reading becomes the whole game rather than a finishing trick.",
        ],
      },
      {
        heading: "Pause and return at any time",
        paragraphs: [
          "The grid, elapsed time, and undo history save automatically in local browser storage. You can close the page and continue the same puzzle later on this device.",
        ],
      },
    ],
  },
  {
    path: "/nonogram/hard",
    ogImage: "/og/hard.png",
    ogImageAlt: "Nonogram Now — a 15 by 15 nonogram grid",
    title: "Hard Nonogram Puzzles — Free 15×15 grids",
    description: "Challenge yourself with a free hard 15×15 nonogram puzzle, uniquely solvable and built for focused play.",
    eyebrow: "15×15 · Hard",
    heading: "Hard Nonogram puzzle",
    lede: "A larger grid for experienced solvers who enjoy a longer chain of deductions.",
    faqs: [
      { question: "How long does a 15×15 nonogram take?", answer: "Typically fifteen to forty minutes. The grid rewards patience: most of the time goes into short deductions that each unlock one more line." },
      { question: "Does a 15×15 grid fit on a phone?", answer: "The squares stay large enough to tap, so on a narrow screen the board pans instead of shrinking. Use the Move tool or drag the number gutter; the row and column clues stay pinned while you pan." },
      { question: "Is guessing ever needed on hard puzzles?", answer: "No. Hard puzzles are longer, not ambiguous — every one is verified to have a single solution reachable by deduction." },
      { question: "What is the fastest way to make progress?", answer: "Work the most constrained lines first: any line whose clues plus their mandatory gaps nearly fill its length has squares that must be filled whichever way the groups slide." },
    ],
    relatedPaths: ["/nonogram/techniques", "/nonogram", "/", "/how-to-play-nonogram"],
    sections: [
      {
        heading: "A deeper picture-logic challenge",
        paragraphs: [
          "Hard puzzles use a 15×15 grid with longer clue sequences and more interactions between rows and columns. The solution never requires guessing, but progress may depend on combining several small deductions.",
        ],
      },
      {
        heading: "Playing 15×15 on a phone",
        paragraphs: [
          "The squares stay large enough to tap; the board pans instead of shrinking. Use the Move tool, or drag from the clue gutter, so a pan does not mark a cell by accident. Start with the fullest lines: add the clue lengths, then add one empty square between each pair of groups. If that total is close to 15, the overlapping squares are filled whichever way the groups slide — mark those first, then pan to the crossing lines they unlock.",
        ],
      },
      {
        heading: "Controls that support careful play",
        paragraphs: [
          "Drag to mark a run of cells, use crosses to protect known gaps, and undo the latest action when needed. On narrow screens the board scrolls in a controlled area without changing the puzzle.",
        ],
      },
    ],
  },
  {
    path: "/nonogram/20x20",
    ogImage: "/og/hard.png",
    ogImageAlt: "Nonogram Now — a 20 by 20 nonogram grid",
    title: "20×20 Nonogram Puzzles — Free large grids",
    description: "Free 20×20 nonogram puzzles with room for real detail — uniquely solvable, no guessing, playable on phone and desktop.",
    eyebrow: "20×20 · Grand",
    heading: "20×20 Nonogram puzzle",
    lede: "The long sitting: a grid with room for outlines and interiors in one picture.",
    faqs: [
      { question: "How long does a 20×20 nonogram take?", answer: "Usually forty minutes to an hour and a half. The picture carries more detail than a 15×15, and most of the extra time goes into interior squares rather than harder logic." },
      { question: "Can I play a 20×20 on a phone?", answer: "Yes. The board pans inside its own area with the row and column clues pinned to the edges, and the corner map shows where you are — tap it to jump. Many players prefer a desktop for the grandest grids." },
      { question: "Do 20×20 puzzles need guessing?", answer: "Never. Every grid is verified to have exactly one solution, and the Logic readout on the page tells you how deep the reasoning goes before you start." },
    ],
    relatedPaths: ["/nonogram/25x25", "/nonogram/hard", "/nonogram/techniques", "/"],
    sections: [
      {
        heading: "A picture with an interior",
        paragraphs: [
          "Twenty squares on a side is where a nonogram stops being an icon and starts being a drawing: an owl with feather texture, a lighthouse with brick courses, a whale with a fin and a tail. The deductions chain longer than a 15×15 without ever needing a guess.",
        ],
      },
      {
        heading: "Finding your way around a big grid",
        paragraphs: [
          "The board keeps its clues pinned while you pan, and the corner map mirrors the whole grid: filled squares show dark, crosses show light, and the outline marks the part you are looking at. Tap anywhere on it to bring that region into view. The zoom you choose is remembered for the size, so a 20×20 you come back to opens where you left it.",
        ],
      },
      {
        heading: "Made for one long sitting",
        paragraphs: [
          "Progress saves itself as you work, on this device, with no account. Start on a lunch break, finish in the evening — the grid, the clock, and the crosses will be waiting exactly as you left them.",
        ],
      },
    ],
  },
  {
    path: "/nonogram/25x25",
    ogImage: "/og/hard.png",
    ogImageAlt: "Nonogram Now — a 25 by 25 nonogram grid",
    title: "25×25 Nonogram Puzzles — The biggest free grids",
    description: "Free 25×25 nonogram puzzles — a whole evening in one grid. Uniquely solvable, no guessing, with a corner map for navigation.",
    eyebrow: "25×25 · Epic",
    heading: "25×25 Nonogram puzzle",
    lede: "A whole evening in one grid: the size the biggest hand-drawn pictures need.",
    faqs: [
      { question: "How long does a 25×25 nonogram take?", answer: "Plan for one to three hours. It is a sitting, not a break — which is exactly what the size is for. Progress saves automatically, so it can also be three evenings." },
      { question: "How do I keep my place on such a big grid?", answer: "The clue gutters stay pinned while the board pans, and the corner map shows the whole picture with a frame around the region on screen. Tap the map to jump. Your zoom level is remembered per size." },
      { question: "Is a 25×25 harder than a 15×15?", answer: "It is longer more than it is harder. The techniques are the same — and every grid is verified unique, so patience always beats guessing. The Logic readout says how deep the reasoning runs." },
    ],
    relatedPaths: ["/nonogram/20x20", "/nonogram/hard", "/nonogram/techniques", "/"],
    sections: [
      {
        heading: "The size a scene needs",
        paragraphs: [
          "At twenty-five squares a side a nonogram holds a real subject: a galleon with rigging, a peacock with its fan, a dinosaur from nose to tail. These are the pictures the smaller sizes cannot carry.",
        ],
      },
      {
        heading: "Built for long work",
        paragraphs: [
          "The pinned clue gutters, the corner map, per-size zoom memory, and automatic saving exist for exactly this grid. Crosses stay where you put them, the undo history runs fifty moves deep, and nothing asks you to start over.",
        ],
      },
      {
        heading: "Verified like every other size",
        paragraphs: [
          "Big does not mean ambiguous. Each 25×25 is checked to have one solution and one only — the same promise the five-minute grids keep.",
        ],
      },
    ],
  },
  {
    path: "/how-to-play-nonogram",
    ogImage: "/og/how-to-play.png",
    ogImageAlt: "Nonogram Now — how to read nonogram clues",
    title: "How to play Nonogram — Interactive beginner guide",
    description: "Learn Nonogram with a short interactive 5×5 lesson covering number clues, filled squares, crosses, rows, and columns.",
    eyebrow: "Learn by doing",
    heading: "How to play Nonogram",
    lede: "Use number clues to find groups of filled squares, then try the same logic in a short interactive 5×5 lesson.",
    faqs: [
      { question: "What do the numbers in a nonogram mean?", answer: "Each number is the length of one unbroken group of filled squares in that row or column, listed in the order the groups appear. Two numbers mean two separate groups with at least one empty square between them." },
      { question: "What does a cross mean?", answer: "A cross marks a square you have proved must stay empty. It never counts as filled; it exists so the remaining space in a line is easier to measure." },
      { question: "How do I start a nonogram?", answer: "Find the line where the clues leave the least slack. If the groups plus their mandatory gaps nearly fill the line, the overlapping squares are filled in every possible arrangement, and those squares are safe to mark." },
      { question: "Can every nonogram be solved without guessing?", answer: "Every puzzle on this site can. Each one is checked by a solver before publication and kept only if deduction alone reaches the single solution." },
    ],
    relatedPaths: ["/nonogram/easy", "/nonogram/techniques", "/", "/nonogram"],
    sections: [
      {
        heading: "What the numbers mean",
        paragraphs: [
          "Each number is the length of one unbroken group of filled squares in that row or column, listed in the order the groups appear. Two numbers, such as 1 1, mean two separate groups with at least one empty square between them. A single 5 on a 5×5 line fills every square in that line.",
        ],
      },
      {
        heading: "A worked 5×5 lesson",
        paragraphs: [
          "This walkthrough uses column clues 2, 4, 4, 4, 2 and row clues 1 1, 5, 5, 3, 1 — the same grid as the interactive lesson on this page.",
          "Step 1 — Read a full row. The second row is a 5 on a five-wide grid, so every square in that row is filled. Always start with the line whose clues leave the least slack.",
          "Step 2 — Close a finished group. The first column’s clue is 2, and those two filled squares are already in place, so the rest of that column cannot be filled. Mark those squares with crosses. Crosses are not filled cells; they protect gaps you have proved.",
          "Step 3 — Combine both directions. Row 1 needs two separate groups of 1. Both filled squares are already known from the columns, so its remaining open squares must stay empty. Every confident mark gives the crossing clues less room to move.",
        ],
      },
      {
        heading: "Mark what cannot be filled",
        paragraphs: [
          "Use crosses to rule out squares. They do not count as filled cells, but they make the remaining spaces easier to measure. Avoid guessing: every puzzle here has one verified solution.",
        ],
      },
      {
        heading: "A line's leftover space is its slack",
        paragraphs: [
          "Add the lengths of the groups, then add one empty square between each pair of groups. Subtract that total from the line length. The remainder is how far the groups can slide. When the remainder is 0, the line is completely determined. When it is small, some squares are filled in every possible placement — those are the squares to mark first, before you look anywhere else.",
        ],
      },
    ],
  },
  {
    path: "/nonogram/techniques",
    title: "Nonogram Solving Techniques — Slack, overlap, edges, forcing",
    description: "The four line techniques that solve a nonogram without guessing: measure the slack, take the overlap, work the edges, then force the runs.",
    eyebrow: "Line logic",
    heading: "Nonogram solving techniques",
    lede: "Almost every deduction in a nonogram happens inside a single line. These are the four things a line will tell you, in the order it tells you them.",
    faqs: [
      { question: "Which nonogram technique should I learn first?", answer: "Overlap. It is the only technique that fills squares in a line you know nothing else about, so it is what gets an empty grid started. Every other technique needs at least one square already settled." },
      { question: "Do I ever need to guess in a nonogram?", answer: "No. Every puzzle on this site is verified to have exactly one solution reachable by deduction, so a square you cannot justify is a square that some other line settles first. Guessing is also expensive: a wrong branch stays consistent for a dozen moves before it collapses, and by then you cannot tell which mark was the bad one." },
      { question: "What does it mean when a line has zero slack?", answer: "It means the clues and their mandatory gaps use the whole line, so there is exactly one way to arrange them. Fill it in completely — a zero-slack line is the cheapest deduction on the board and worth scanning for before anything else." },
      { question: "How do I solve a 15×15 without losing track?", answer: "Sweep rather than wander. Work every row top to bottom, then every column left to right, then start again. Each pass leaves the next one more to work with, and a full pass is how you notice that a line has become solvable." },
    ],
    relatedPaths: ["/nonogram/techniques/overlap", "/nonogram/techniques/edge", "/nonogram/techniques/forcing", "/nonogram/techniques/gaps"],
    sections: [
      {
        heading: "Every deduction happens inside one line",
        paragraphs: [
          "A nonogram looks like a grid problem and is solved as a stack of one-dimensional ones. You take a single row or column, work out everything its own clues force, then move on. What makes the grid a grid is only this: a square settled in a row changes the column crossing it, which is why a line that gave nothing on the first pass often gives something on the third.",
        ],
      },
      {
        heading: "Measure the slack before anything else",
        paragraphs: [
          "Add the clue lengths together, then add one empty square between each neighbouring pair. Subtract that total from the length of the line. What is left is the slack — how far the whole arrangement can slide. A 10-wide line with clues 4 3 needs 4 + 1 + 3 = 8 squares, so its slack is 2.",
          "Slack is the number every other technique is measured against. At slack 0 the line is completely determined and you fill it outright. When a clue is longer than the slack, that clue covers squares no matter where it sits. When every clue is shorter than the slack, the line has nothing to give yet and your attention is worth more somewhere else.",
        ],
      },
      {
        heading: "The four techniques, in the order a line gives them up",
        paragraphs: [
          "Overlap comes first: any clue longer than the slack fills squares before you know where its run actually is. Edge logic comes next, because once a square at the end of a line is settled the first clue either starts there or is pushed past it, and both outcomes settle more squares.",
          "Forcing follows: a filled square that only one clue can reach pins that clue, which usually collapses the rest of the line. Gap analysis closes it — a stretch of open squares shorter than any clue that could still fit there gets crossed out entirely, and those crosses are what the crossing lines read next.",
        ],
      },
      {
        heading: "Sweep rows, then columns, then repeat",
        paragraphs: [
          "Work every row top to bottom, then every column left to right, then start again. Do not chase one interesting line around the grid: the value of a sweep is that each pass leaves the next one more to work with, and a full pass is how you notice it. Most 15×15 grids come apart in three or four sweeps.",
          "If a complete sweep of both directions adds nothing at all, the puzzle has not stalled — your reading of it has. Recount the slack on the two or three longest lines. A miscounted gap is a far more common cause than a genuinely hard deduction.",
        ],
      },
    ],
  },
  {
    path: "/nonogram/techniques/overlap",
    title: "Nonogram Overlap Technique — The arithmetic and worked lines",
    description: "Any clue longer than its line's slack fills squares in every arrangement. The subtraction that finds them, plus two worked lines you can step through.",
    eyebrow: "The first technique",
    heading: "The overlap technique",
    lede: "The first thing to try on any line: when a clue is longer than the line's slack, it fills squares before you know where its run actually sits.",
    faqs: [
      { question: "How do I calculate a nonogram overlap?", answer: "Subtract the slack from the clue. Slack is the line's length minus the clues and the mandatory gaps between them; a clue of length c on a line with slack s covers c − s squares in every arrangement. If c − s is zero or less, that clue overlaps nothing." },
      { question: "Why are the overlapping squares always in the middle of the run?", answer: "Because a run is unbroken. Slide it as far left as it goes and as far right as it goes, and the squares it covers at both extremes are the ones it cannot avoid in between — those sit in the middle of its range of travel." },
      { question: "Does overlap work when a line has several clues?", answer: "Yes, and each clue is measured separately against the same slack, because slack belongs to the line rather than to one clue. On a 15-wide line with clues 4 6 the slack is 4, so the 4 pays nothing and the 6 pays two squares. One clue paying and the other not is the normal case." },
      { question: "What do I do when a line has no overlap?", answer: "Leave it. No overlap means the line cannot say anything until a crossing square is settled, and those squares come from the lines that did pay out. Coming back to it after a full sweep costs nothing; staring at it costs the sweep." },
    ],
    relatedPaths: ["/nonogram/techniques/edge", "/nonogram/techniques", "/nonogram/techniques/forcing", "/nonogram/hard"],
    sections: [
      {
        heading: "Push the run both ways",
        paragraphs: [
          "Take a run as far left as it will go, then as far right as it will go. Those are the two extremes of everywhere it could possibly be. Any square the run covers in both extremes is a square it covers in every arrangement in between, because a run is unbroken — it cannot skip over a square it spans at both ends of its travel.",
        ],
      },
      {
        heading: "The arithmetic",
        paragraphs: [
          "Slack is the line's length minus the clues and their mandatory gaps. A clue of length c on a line with slack s covers c − s squares in every arrangement. When c − s is zero or less, that clue overlaps nothing and you move on.",
          "Take a 7 on a 10-wide line. It is the only clue, so the slack is 10 − 7 = 3 and the overlap is 7 − 3 = 4 squares. They sit at squares 4 through 7, in the middle of the run's travel. Four squares of a line you otherwise know nothing about, from one subtraction.",
        ],
      },
      {
        heading: "Several clues share one slack",
        paragraphs: [
          "Slack belongs to the line, not to any single clue, so the same subtraction runs against each clue separately. On a 15-wide line with clues 4 6, the minimum length is 4 + 1 + 6 = 11 and the slack is 4. The 4 overlaps nothing, because 4 − 4 = 0. The 6 overlaps two squares, because 6 − 4 = 2. One clue paying out while its neighbour pays nothing is the ordinary case, not a sign you have miscounted.",
        ],
      },
      {
        heading: "Where overlap is worth spending",
        paragraphs: [
          "Overlap pays best on the longest clue in the tightest line, so scan for lines whose numbers nearly fill them rather than working in grid order on the first pass. On a 15×15 board the opening sweep usually seeds a dozen or so squares this way, and every later deduction is built on those.",
        ],
      },
      {
        heading: "When overlap gives nothing",
        paragraphs: [
          "If every clue is shorter than or equal to the slack, the line has no overlap at all. That is not a stall. It means this line needs a crossing square before it can say anything, and crossing squares come from the lines that did pay out — which is the whole reason for sweeping the board in both directions instead of solving one line to the end.",
        ],
      },
    ],
  },
  {
    path: "/nonogram/techniques/edge",
    title: "Nonogram Edge Logic — What the ends of a line give you",
    description: "A run has least room at the ends of a line, so one square settled there settles several more. Two worked lines, including one overlap cannot touch.",
    eyebrow: "The second technique",
    heading: "Edge logic",
    lede: "The ends of a line are where a run has the least room to move. That is what makes an edge square worth reading first: settling one there usually settles several more.",
    faqs: [
      { question: "What is edge logic in a nonogram?", answer: "It is what you can deduce from a square at either end of a line. A filled square in the first position can only belong to the first clue, and that clue has nowhere to start except right there — so the whole run drops into place, and the square after it is empty." },
      { question: "Why does a filled square at the edge do more than one in the middle?", answer: "Because a run reaching a middle square can be positioned either side of it, while a run reaching the first square has only one way to sit. The edge removes the ambiguity that makes middle squares slow to resolve." },
      { question: "Is a cross at the edge worth anything?", answer: "Yes, and often as much as a fill. A crossed-out square at the end shortens the usable line by one, which lowers the slack for every clue in it. On a 10-wide line with a single clue of 7, one cross at square 1 turns a four-square overlap into a five-square one." },
      { question: "Should I check edges before or after overlap?", answer: "Overlap first on an empty grid, because it needs nothing to be known. After that, check the edges of every line that has picked up a mark — edge logic is the technique most likely to pay on a line overlap has already been over." },
    ],
    relatedPaths: ["/nonogram/techniques/forcing", "/nonogram/techniques", "/nonogram/techniques/gaps", "/nonogram/hard"],
    sections: [
      {
        heading: "The first square belongs to the first clue",
        paragraphs: [
          "A run cannot start before the line does, so a filled square in the first position can only be the beginning of the first clue. The whole run drops into place from there, and the square immediately after it must be empty — otherwise the run would be longer than its clue says.",
          "Take a 15-wide line with clues 5 4. Its slack is 5, so neither clue is longer than the slack and overlap finds nothing at all. Fill in square 1 from a crossing line and the picture changes completely: the 5 has to occupy squares 1 to 5, and square 6 has to be empty. One square bought five.",
        ],
      },
      {
        heading: "A cross at the edge is worth as much as a fill",
        paragraphs: [
          "Crossing out a square at the end of a line shortens the line the runs actually have to live in, and a shorter line means less slack for every clue in it. This is the deduction players skip most often, because a cross feels like the absence of information rather than information.",
          "A 10-wide line with a single clue of 7 has slack 3 and overlaps four squares. Cross out square 1 and the clue now lives in nine squares instead of ten, so the slack falls to 2 and the overlap grows to five squares. The cross paid exactly as much as filling a square would have.",
        ],
      },
      {
        heading: "Both ends, every pass",
        paragraphs: [
          "Edge logic applies at the last square exactly as it does at the first — a filled square in the final position belongs to the last clue, and the square before the run is empty. Reading a line right to left is not a different technique, and lines whose right end you never check are the usual reason a sweep stalls halfway.",
        ],
      },
      {
        heading: "When the edge is not settled yet",
        paragraphs: [
          "If neither end of a line carries a mark, edge logic has nothing to work with, and the line is waiting on a crossing square rather than on you. That is the normal state early in a grid. It is also why the opening sweep is overlap's job: overlap is the only technique that pays on a line where nothing at all is known.",
        ],
      },
    ],
  },
  {
    path: "/nonogram/techniques/forcing",
    title: "Nonogram Forcing — Pinning a run with one filled square",
    description: "A filled square only one clue can reach tells you which clue it is, and pins the run. Two worked lines — including one that buys you nothing.",
    eyebrow: "The third technique",
    heading: "Forcing",
    lede: "Ask which clue a filled square could possibly belong to. When only one can reach it, that run is pinned, and the rest of the line usually follows.",
    faqs: [
      { question: "What is forcing in a nonogram?", answer: "Working out which clue a filled square belongs to. Each clue can only reach a limited stretch of the line, so a filled square outside every other clue's reach must belong to the one clue that reaches it — and knowing which run it is constrains where that run starts." },
      { question: "How do I know how far a clue can reach?", answer: "Pack every run as far left as it goes to find the earliest square each one can occupy, then pack them as far right for the latest. A clue reaches everything between those two extremes and nothing outside them. A filled square inside exactly one clue's reach is a forcing square." },
      { question: "What if two clues can both reach the square?", answer: "Then forcing tells you nothing yet, and the honest move is to leave the line. Check whether the square you were about to claim was already forced without it — a deduction that only re-proves what overlap gave you is a deduction that cost you the sweep." },
      { question: "Does forcing ever prove a square is empty?", answer: "Often, yes. Once a run is pinned and its full length is accounted for, the squares immediately before and after it cannot be filled, so they get crossed out. Those crosses are usually worth more to the crossing lines than the filled squares are." },
    ],
    relatedPaths: ["/nonogram/techniques/gaps", "/nonogram/techniques", "/nonogram/techniques/edge", "/nonogram/hard"],
    sections: [
      {
        heading: "Every clue has a reach",
        paragraphs: [
          "Pack the runs as far left as they go and you have the earliest square each clue can occupy. Pack them as far right and you have the latest. Between those two positions is that clue's reach, and it can never touch a square outside it. Reaches overlap near the middle of a line and separate towards the ends.",
          "So when you meet a filled square, ask which reaches contain it. If the answer is one, that square belongs to that run, and you have learned something no amount of staring at the clue list would give you.",
        ],
      },
      {
        heading: "A pinned run drags the rest of the line with it",
        paragraphs: [
          "Take a 15-wide line with clues 1 1 6. Its slack is 5, so overlap forces a single square — square 10 — and nothing else. Now fill in square 15 from a crossing line. Neither 1 can reach that far, so the 6 owns it, and the only way a run of six covers square 15 is by occupying squares 10 to 15 exactly.",
          "The run is now pinned, which also settles what is around it: square 9 has to be empty, because a filled square there would make the run seven long. One square from a crossing line turned into five more filled squares and a cross.",
        ],
      },
      {
        heading: "Check that the deduction is actually new",
        paragraphs: [
          "Forcing is the technique that most often feels productive while producing nothing. On a 12-wide line with clues 2 5, square 8 is already forced by overlap alone. Fill in square 6 and it looks as though the 5 has been pinned — but the only square you can now claim is the one overlap had already given you.",
          "The test is quick: before marking anything, ask whether this line would have given you the same square with nothing known. If it would, you have spent attention rather than earned ground, and the line still needs a crossing square before it will say anything new.",
        ],
      },
      {
        heading: "Where forcing pays best",
        paragraphs: [
          "Look for filled squares near the ends of long lines with several clues. That is where the reaches have separated, so a single clue owns the square outright, and it is also where the pinned run is long enough for the deduction to be worth having. Filled squares in the middle of a line with generous slack are usually reachable by two clues at once, and those are the ones to leave alone.",
        ],
      },
    ],
  },
  {
    path: "/nonogram/techniques/gaps",
    title: "Nonogram Gap Analysis — Crossing out space no clue can use",
    description: "A stretch too short for any clue that can still reach it is empty end to end. The rule, a line that solves outright, and the segment you must not cross.",
    eyebrow: "The fourth technique",
    heading: "Gap analysis",
    lede: "Once a line has a cross in it, it is no longer one line but two. Measure each piece against the clues that could still live there, and the pieces too short to hold any of them are empty end to end.",
    faqs: [
      { question: "What is gap analysis in a nonogram?", answer: "Measuring the stretches of open squares a line has been cut into, and crossing out any stretch too short to hold a clue that could still reach it. It is the technique that turns one cross into several, which is why it usually runs last on a line rather than first." },
      { question: "How do I know which clues can still reach a segment?", answer: "A clue can only reach a segment if every clue before it still fits in the space to the left, and every clue after it still fits to the right. If no clue passes that test for a given segment, the whole segment is empty." },
      { question: "Why are crosses worth as much as filled squares?", answer: "Because the crossing lines read them. A cross tells the line running through it that a run stops there, which is what turns a long open line into two short measurable ones. Players who only mark filled squares lose most of the information a solved line produces." },
      { question: "When does gap analysis run in a sweep?", answer: "After the line has picked up marks from overlap, an edge, or a forcing square. It needs at least one cross or a completed run to have something to measure against, and it pays best immediately after a run has been closed off." },
    ],
    relatedPaths: ["/nonogram/techniques", "/nonogram/techniques/overlap", "/nonogram/techniques/forcing", "/nonogram/hard"],
    sections: [
      {
        heading: "A cross cuts a line into segments",
        paragraphs: [
          "A crossed-out square is a wall. Nothing passes through it, so a line carrying one cross is really two shorter lines, and each of them has to hold whichever clues can still reach it. That is the whole technique: stop looking at the line and start looking at its pieces.",
          "Take a 12-square line with a single clue of 5. Its slack is 7, so overlap finds nothing at all. Now cross out square 4. The piece to its left is three squares long and a run of five cannot fit in three, so squares 1 to 3 are empty as well — and the piece to the right is eight squares holding a 5, which overlaps squares 8 and 9. One cross produced three more crosses and two filled squares.",
        ],
      },
      {
        heading: "Closing one segment can close the line",
        paragraphs: [
          "When the surviving segment is exactly as long as the clues it must hold, the line has no slack left anywhere and it resolves completely. This is the most valuable thing gap analysis does, and it happens more often than it sounds.",
          "A 15-square line with clues 4 6 gives you squares 10 and 11 from overlap and no more. Cross out square 4 and squares 1 to 3 go with it, because neither clue fits in three squares. That leaves squares 5 to 15 — eleven squares for 4 + 1 + 6 = 11. There is exactly one arrangement left: the 4 on squares 5 to 8, square 9 empty, the 6 on squares 10 to 15. The whole line, from one cross.",
        ],
      },
      {
        heading: "The segment you must not cross out",
        paragraphs: [
          "The rule is that a segment is empty when no clue that could still reach it fits — not when the biggest clue does not fit. Checking only the largest number is the way this technique goes wrong, and it goes wrong quietly, because a wrongly crossed square stays consistent for a long time before the grid contradicts it.",
          "Change the clues to 2 6 and cross square 4 again. The three squares on the left still cannot hold the 6, but they hold the 2 comfortably, so nothing there can be crossed. The cross was not worthless — it shortens the space the 6 has to live in, which fills square 10 — but the three squares you were about to claim are not yours.",
        ],
      },
      {
        heading: "Mark the crosses, not just the fills",
        paragraphs: [
          "Gap analysis only works on a board where crosses are actually written down, because the segments it measures are the ones the crosses create. A player who marks filled squares and leaves proved-empty squares blank keeps the information in their head, where the crossing lines cannot read it, and then wonders why a grid that should sweep clean has stalled.",
        ],
      },
    ],
  },
  {
    path: "/daily-nonogram/archive",
    title: "Daily Nonogram Archive — Play the last 30 puzzles",
    description: "Play the last 30 uniquely solvable Daily Nonogram puzzles, with stable grids for every UTC date.",
    eyebrow: "Thirty days of quiet clues",
    heading: "Daily Nonogram archive",
    lede: "Missed a day? Choose a recent puzzle. Each date always opens the same uniquely solvable grid.",
    faqs: [
      { question: "How far back does the archive go?", answer: "The previous 30 daily puzzles. Each date always opens the same grid, so a puzzle you started yesterday is the one you come back to." },
      { question: "Do archived puzzles count towards my streak?", answer: "The streak counts days you completed the daily puzzle. Playing an older date is a way to catch up on a picture you missed rather than a way to repair a broken streak." },
    ],
    relatedPaths: ["/", "/collection", "/nonogram", "/how-to-play-nonogram"],
    sections: [
      {
        heading: "Return to a puzzle you missed",
        paragraphs: ["The archive keeps the previous 30 daily grids available. Dates use UTC, so a puzzle remains consistent across devices, regions, and later deployments."],
      },
      {
        heading: "A date is a saved game",
        paragraphs: [
          "Opening 12 August always loads that day's grid, including any progress this browser already saved for it. Finishing an old date adds the picture to your collection; it does not patch a broken streak. Streaks only count days you completed on the day itself.",
        ],
      },
      {
        heading: "Choose a challenge that fits the day",
        paragraphs: ["Weekday puzzles use a balanced 10×10 grid, Sundays use a quick 5×5 grid, and Saturdays offer a longer 15×15 challenge. Every archived date keeps its original size and solution."],
      },
    ],
  },
  {
    path: "/collection",
    title: "Nonogram Collection — Every picture you have solved",
    description: "Track every hidden picture you have revealed. Frames stay locked until you solve the grid, so nothing is spoiled in advance.",
    eyebrow: "Your picture collection",
    heading: "Collection",
    lede: "Every puzzle hides a picture. Solve one and it is added here — with its name — so the set fills in as you play.",
    faqs: [
      { question: "How do pictures get into my collection?", answer: "By solving them. A frame stays locked until the last square of its grid is filled, and puzzle addresses never carry the answer, so the set only fills in with pictures you actually solved." },
      { question: "Is my collection synced between devices?", answer: "No. It lives in this browser's local storage, with no account and nothing uploaded, so another browser or device starts with an empty collection." },
    ],
    relatedPaths: ["/", "/nonogram", "/daily-nonogram/archive", "/how-to-play-nonogram"],
    sections: [
      {
        heading: "The picture is the reward",
        paragraphs: ["Nothing is revealed in advance. A frame stays locked until you have filled its last square, and puzzle addresses never carry the answer, so a collection you fill in is one you actually solved."],
      },
      {
        heading: "What the shelves hold",
        paragraphs: [
          "Pocket is 5×5 silhouettes you can read in one glance. Studio is the everyday 10×10 pictures. Gallery is 15×15 with room for interior detail. Alphabet is the A–Z and 0–9 set, kept on its own shelf so a wall of letters does not bury the pictures. A locked frame has no name and no theme — even the category would narrow the guess.",
        ],
      },
      {
        heading: "Kept on this device",
        paragraphs: ["Progress lives in this browser's local storage. There is no account to make and nothing is uploaded, which also means a different browser starts with an empty collection."],
      },
    ],
  },
  {
    path: "/printable-nonograms",
    title: "Printable Nonogram — Free puzzle and answer sheet",
    description: "Print a free beginner 5×5 nonogram puzzle and its answer key on A4 or Letter paper.",
    eyebrow: "Paper mode",
    heading: "Printable Nonogram",
    lede: "A clean 5×5 puzzle and its answer key, formatted for ordinary A4 or Letter paper.",
    printableSheet: { size: 5, ids: [PRINTABLE_PUZZLE_ID] },
    faqs: [
      { question: "What is on the printed sheets?", answer: "Two pages: the first has the row and column clues with an empty grid, the second has the completed pattern. Print them together or keep the answer aside until you are done." },
      { question: "Will it print correctly on my paper size?", answer: "Yes. The layout fits both A4 and Letter, and the grid uses black marks only, so it stays readable on a black-and-white printer." },
      { question: "Can I save these as a PDF instead of printing?", answer: "Yes, and it needs nothing extra. Choose Print, then pick “Save as PDF” as the destination — Chrome, Safari, Edge and Firefox all offer it, as does the print dialog on iPhone and Android. The file you get is the sheet exactly as it would have printed, answer key included." },
    ],
    relatedPaths: ["/printable-nonograms/5x5", "/printable-nonograms/10x10", "/printable-nonograms/15x15", "/printable-nonograms/for-kids"],
    sections: [
      {
        heading: "Print a puzzle with its answer",
        paragraphs: [
          "The first sheet contains row and column clues with an empty grid. The second sheet, further down, contains the completed pattern — skip it if you want to solve first.",
        ],
      },
      {
        heading: "How to solve it on paper",
        paragraphs: [
          "Keep the answer page face down. Work one line at a time: pencil in squares you are sure of, then put a light × on squares you have proved empty. When a line is finished, tick its clues so you do not recount them. The printed grid is 5×5, so a clue of 5 fills a line and a clue of 4 covers the middle three squares.",
        ],
      },
      {
        heading: "Made for an ordinary home printer",
        paragraphs: ["The grid uses clear black marks without relying on color, and the layout fits both A4 and Letter paper. Print the two sheets together or keep the answer page aside until the puzzle is complete."],
      },
    ],
  },
  {
    path: "/printable-nonograms/5x5",
    title: "Printable 5×5 Nonograms — Four puzzles, one sheet",
    description: "Print four beginner 5×5 nonogram puzzles two-up on a single A4 or Letter page, with the answer key on the sheet behind it.",
    eyebrow: "One sheet · four grids",
    heading: "Printable 5×5 Nonograms",
    lede: "Four small grids on one page, sized to be written in with an ordinary pencil, with their answers on the sheet behind.",
    printableSheet: PRINTABLE_SHEET_5,
    faqs: [
      { question: "How many 5×5 puzzles fit on one page?", answer: "Four, printed two across and two down. That is the largest number that still leaves each square big enough to pencil a cross into without the marks running together." },
      { question: "How long does a 5×5 nonogram take on paper?", answer: "One to three minutes each once you know the rules, so a sheet of four is roughly a ten-minute sitting. Five clues per line means every deduction is visible without counting far ahead." },
      { question: "Can I print these without the answers?", answer: "Yes. The builder on the main printable page has an answer-key switch, and turning it off prints the puzzle sheets alone. This page always includes the key, on the last sheet, so it can be kept face down." },
      { question: "Will it print correctly on A4 and on Letter?", answer: "Yes. The layout is built for the 182×251mm safe area the two paper sizes share, and the grids use black marks only, so nothing depends on a colour printer." },
    ],
    relatedPaths: ["/printable-nonograms/10x10", "/printable-nonograms/for-kids", "/printable-nonograms", "/nonogram/easy"],
    sections: [
      {
        heading: "What comes out of the printer",
        paragraphs: [
          "Two pages. The first has four 5×5 grids with their row and column clues and a line for a name, a date and a time. The second is the answer key, with all four solved pictures drawn small enough to sit side by side.",
          "The grids are the same puzzles every time this page is printed, so a sheet handed to somebody else is the sheet they can compare answers on. If you want a different set, the builder on the main printable page draws one and puts the draw in the address.",
        ],
      },
      {
        heading: "Why 5×5 is the size to print first",
        paragraphs: [
          "A five-wide line holds the whole deduction in view: a clue of 5 fills the line outright, and a clue of 4 always covers the middle three squares whichever way it slides. On paper that matters more than on screen, because there is no undo — you want to be sure of a square before the pencil touches it.",
        ],
      },
      {
        heading: "Working on paper",
        paragraphs: [
          "Keep the answer page face down. Work one line at a time: pencil in the squares you are sure of, then put a light × on the squares you have proved empty. Tick a clue once its run is complete so you do not recount it. Crosses are the marks that make the crossing clues measurable, and on paper they are the ones people skip.",
        ],
      },
    ],
  },
  {
    path: "/printable-nonograms/10x10",
    title: "Printable 10×10 Nonograms — Two puzzles with answer key",
    description: "Print two 10×10 nonogram puzzles, one per page at a size worth writing in, with a shared answer key on the last sheet.",
    eyebrow: "Two sheets · one grid each",
    heading: "Printable 10×10 Nonograms",
    lede: "The balanced size: long enough that a line needs real deduction, short enough to finish in a sitting. One grid per page, at a size worth writing in.",
    printableSheet: PRINTABLE_SHEET_10,
    faqs: [
      { question: "Why is there only one 10×10 per page?", answer: "Because two would halve the squares. A 10×10 drawn at a size you can pencil a cross into takes most of the printable area on its own, and a grid too small to mark is a grid nobody finishes." },
      { question: "How long does a 10×10 take?", answer: "Fifteen to twenty-five minutes for most people. The clue lists are long enough that the first pass rarely settles a line outright, so it is worth sweeping all ten rows before starting on the columns." },
      { question: "Are these harder than the 5×5 sheets?", answer: "Longer rather than harder. Every puzzle here is checked to have one solution reachable by deduction alone, so no square ever needs a guess — but there are a hundred of them instead of twenty-five, and the chains of reasoning run further." },
      { question: "How many pages will print?", answer: "Three: one page per puzzle, then the answer key with both solved pictures on it." },
    ],
    relatedPaths: ["/printable-nonograms/15x15", "/printable-nonograms/5x5", "/printable-nonograms", "/nonogram"],
    sections: [
      {
        heading: "What comes out of the printer",
        paragraphs: [
          "Three pages: one 10×10 grid per sheet with its clues and a header line for a name, a date and a time, then an answer key with both solved pictures. Print all three and keep the last one aside, or stop after two if you would rather not have the answers in the room.",
        ],
      },
      {
        heading: "Where to start on a 10×10",
        paragraphs: [
          "Find the line with the least slack — add the clues, add one square between each pair, and subtract from ten. A line whose numbers nearly fill it has squares that are filled wherever its runs sit, and those are the squares every later deduction is built on.",
          "After that, sweep. Work all ten rows top to bottom, then all ten columns left to right, then start again. Each pass leaves the next one more to work with, and on paper a full pass is also how you notice a line you miscounted the first time.",
        ],
      },
      {
        heading: "Printing notes",
        paragraphs: [
          "The layout fits both A4 and Letter, and the grid uses black marks only, so it stays readable from a black-and-white printer. Print at 100% rather than “fit to page”: scaling shrinks the squares, and the squares are sized to be written in.",
        ],
      },
    ],
  },
  {
    path: "/printable-nonograms/15x15",
    title: "Printable 15×15 Nonograms — Two hard puzzles with answers",
    description: "Print two 15×15 nonogram puzzles, one per page, with longer clue sequences and deeper chains of deduction. Answer key on the last sheet.",
    eyebrow: "Two sheets · one grid each",
    heading: "Printable 15×15 Nonograms",
    lede: "Two hundred and twenty-five squares, longer clue sequences, and deductions that run several lines deep. One grid per page, answers on the last sheet.",
    printableSheet: PRINTABLE_SHEET_15,
    faqs: [
      { question: "How long does a 15×15 nonogram take?", answer: "Forty minutes to an hour and a half, depending on how disciplined the sweeps are. It is the size where working the whole board in passes beats chasing one interesting line, because most lines only become solvable after a crossing line has settled something." },
      { question: "Do I need to guess on a 15×15?", answer: "No. Every puzzle here is verified to have exactly one solution reachable by deduction. If a square will not resolve, another line settles it first — usually one you have not swept since the last few marks went in." },
      { question: "Is a 15×15 too big to print on one page?", answer: "No, but it needs the whole printable area, which is why there is one grid per sheet. Print at 100% rather than fitting to page, or the clue numbers along the edges start to crowd." },
      { question: "What should I do when a 15×15 stalls?", answer: "Recount the slack on the two or three longest lines before looking for a clever deduction. A miscounted gap is a far more common cause of a stalled grid than a genuinely hard step, and on paper it is easy to make." },
    ],
    relatedPaths: ["/printable-nonograms/10x10", "/printable-nonograms", "/nonogram/hard", "/nonogram/techniques"],
    sections: [
      {
        heading: "What comes out of the printer",
        paragraphs: [
          "Three pages: two 15×15 grids, one per sheet, each with its clue lists and a header line for a name, a date and a time, then an answer key with both solved pictures drawn small. A 15×15 is an hour of work, so the header line is there for people who put a puzzle down and come back to it.",
        ],
      },
      {
        heading: "Sweeps, not hunts",
        paragraphs: [
          "On a 15-wide line, a clue of 9 fills three squares before you know anything else, and a clue of 12 fills nine. Take those first, everywhere they exist, and only then start reading the lines that need a crossing square. Working the board in full passes is what turns a 15×15 from a puzzle into a procedure.",
          "Mark the crosses. On a grid this size the proved-empty squares carry more information than the filled ones, because they are what cut a long open line into short measurable pieces — and a piece too short to hold any remaining clue is empty all the way across.",
        ],
      },
      {
        heading: "Printing notes",
        paragraphs: [
          "The sheet is laid out for the 182×251mm area A4 and Letter have in common, and prints correctly on either without changing any setting. Use 100% scale and a sharp pencil: at this size the grid is drawn with a heavier line every five squares, which is what keeps a long row countable.",
        ],
      },
    ],
  },
  {
    path: "/printable-nonograms/for-kids",
    title: "Printable Nonograms for Kids — Eight 5×5 puzzles to hand out",
    description: "Eight beginner 5×5 nonogram puzzles across two sheets, with pictures children recognise and an answer key for whoever is marking them.",
    eyebrow: "Two sheets · eight grids",
    heading: "Printable Nonograms for Kids",
    lede: "Eight small grids across two sheets, each hiding a picture a child will recognise, with one answer key for whoever is marking them.",
    printableSheet: PRINTABLE_SHEET_KIDS,
    faqs: [
      { question: "What age are these suitable for?", answer: "Around seven and up, once a child can count reliably to five. The rule takes about a minute to explain, and the arithmetic never goes past adding a few small numbers — what the puzzle actually trains is holding two constraints in mind at once." },
      { question: "How long does one take?", answer: "Two to five minutes for a child meeting nonograms for the first time, faster after the first two or three. A sheet of four is a comfortable single sitting; the second sheet is there so a group does not run out." },
      { question: "Can I use these in a classroom?", answer: "Yes. The sheets are free to print and hand out, there is no account or watermark, and the answer key is on its own page so it can be kept back. Each grid has a name, date and time line at the top." },
      { question: "What do I tell a child who is stuck?", answer: "Point at the fullest line rather than the square they are staring at. A clue of 5 fills a row of five outright, and a clue of 4 always fills the middle three — those give the first certain squares, and every certain square makes the crossing clues easier." },
    ],
    relatedPaths: ["/printable-nonograms/5x5", "/printable-nonograms", "/how-to-play-nonogram", "/nonogram/easy"],
    sections: [
      {
        heading: "What comes out of the printer",
        paragraphs: [
          "Three pages: two sheets of four 5×5 grids, then one answer key with all eight solved pictures. The hidden pictures are everyday things — a tooth, a boot, a snowflake, a book — so finishing a grid produces something recognisable rather than an abstract pattern.",
          "The puzzles are the same every time this page is printed, so a class working from one sheet is working from the same eight grids, and the key on the last page matches them in order.",
        ],
      },
      {
        heading: "Explaining the rule in a minute",
        paragraphs: [
          "Each number counts a run of filled squares in that row or column, written in the order the runs appear. Two numbers mean two separate runs with at least one empty square between them. A 5 on a five-wide row fills the whole row — that is the square to start from, and it is usually enough for a child to work out the rest without further help.",
          "Show the cross before the fill. A cross marks a square proved empty, and children who only shade squares lose track of which parts of a line are still open. Marking the empties is the habit that makes the puzzles get faster.",
        ],
      },
      {
        heading: "Why these are worth handing out",
        paragraphs: [
          "A nonogram cannot be guessed at successfully — every square follows from the clues, and every one of these grids has been checked by a solver to have exactly one solution reachable by deduction. So a child who finishes one has actually reasoned it out, and a child who is stuck has a specific question to ask rather than a wall to stare at.",
        ],
      },
    ],
  },
  {
    path: "/about",
    title: "About — Nonogram Now",
    description: "Who builds Nonogram Now, how each puzzle is drawn, and why every grid is machine-checked to have exactly one solution before it is published.",
    eyebrow: "Who makes this",
    heading: "About Nonogram Now",
    lede: "A small, deliberately quiet nonogram site: one checked puzzle a day, no account, and nothing asking for your attention while you think.",
    relatedPaths: ["/", "/how-to-play-nonogram", "/collection", "/printable-nonograms"],
    faqs: [
      { question: "Who runs Nonogram Now?", answer: "It is built and maintained by hellosun, working alone. It is a side project rather than a company, so the \u201cwe\u201d in the policy pages is one person." },
      { question: "Where do the puzzle pictures come from?", answer: "Every picture is drawn by hand for this site \u2014 264 of them so far, across animals, food, music, nature, objects, symbols, and weather. A generator turns each drawing into a grid and derives its clues from the finished image." },
      { question: "How do you know a puzzle can be solved without guessing?", answer: "The build runs a solver over every puzzle and counts how many solutions its clues allow. A grid that admits zero solutions, or more than one, fails the build and is never published." },
    ],
    sections: [
      {
        heading: "What this site is",
        paragraphs: [
          "Nonogram Now publishes one picture logic puzzle every UTC day, plus a library you can play at 5\u00d75, 10\u00d710, and 15\u00d715 whenever you like. Everyone opening the site on the same date gets the same daily grid, which is what makes a time or a streak worth comparing.",
          "It exists because most puzzle sites treat the puzzle as the thing wrapped around everything else. Here the grid is the page.",
        ],
      },
      {
        heading: "How the puzzles are made",
        paragraphs: [
          "Every picture is drawn by hand \u2014 264 of them so far, across animals, food, music, nature, objects, symbols, and weather. A generator turns each drawing into a grid and derives its row and column clues.",
          "Nothing ships unverified. The build re-derives the clues from the solution and runs a solver that counts solutions; a puzzle with zero solutions or more than one fails the build and never reaches the site. That check is why careful deduction is always enough, and why you never need to guess.",
        ],
      },
      {
        heading: "Who builds it",
        paragraphs: [
          "Nonogram Now is built and maintained by hellosun, working alone. It is a side project, not a company, and there is no team behind the \u201cwe\u201d in the policy pages.",
        ],
      },
      {
        heading: "What it does not do",
        paragraphs: [
          "There are no accounts, no email list, and nothing to buy. Your unfinished grid, streak, and best times live in your own browser and are never uploaded, so clearing site data clears them and a second device starts fresh.",
          "The site records limited analytics to understand which pages people find useful. What that covers, and what it deliberately does not, is written out on the privacy page.",
          `Questions, bug reports, and suggestions go to ${SITE_EMAIL}.`,
        ],
      },
    ],
  },
  {
    path: "/contact",
    title: "Contact — Nonogram Now",
    description: "Email Nonogram Now about a broken puzzle, a privacy question, an accessibility problem, or a suggestion. One person reads every message.",
    eyebrow: "Get in touch",
    heading: "Contact",
    lede: "One person reads this inbox, so a reply may take a few days \u2014 but every message is read.",
    relatedPaths: ["/", "/how-to-play-nonogram", "/collection", "/printable-nonograms"],
    sections: [
      {
        heading: "Email",
        paragraphs: [`Write to ${SITE_EMAIL}. There is no contact form, no ticket number, and no autoresponder.`],
      },
      {
        heading: "Reporting a broken puzzle",
        paragraphs: ["If a grid seems to have no solution or more than one, that is a bug worth hearing about. Include the date or the puzzle\u2019s size and the clues you were stuck on. Every puzzle is checked by a solver before it ships, so a real counter-example is genuinely useful."],
      },
      {
        heading: "Privacy requests",
        paragraphs: ["The site holds no account and stores nothing about you on its servers, so there is usually no personal data to export or delete \u2014 clearing your browser\u2019s site data removes everything the site kept. If you still have a question about what is collected, this address is the right place to ask."],
      },
      {
        heading: "Everything else",
        paragraphs: ["Suggestions for pictures, notes on the mobile experience, accessibility problems, and questions about reusing the printable sheets are all welcome."],
      },
    ],
  },
  {
    path: "/privacy",
    title: "Privacy — Nonogram Now",
    description: "Understand how Nonogram Now stores progress locally, records limited product events, and uses Cloudflare Web Analytics and Google Analytics.",
    eyebrow: "Plain-language policy",
    heading: "Privacy",
    lede: "Nonogram Now works without an account. Your puzzle progress, streak, and best times stay in your browser.",
    relatedPaths: ["/", "/nonogram", "/how-to-play-nonogram", "/collection"],
    sections: [
      {
        heading: "What stays on your device",
        paragraphs: ["Browser storage remembers unfinished puzzles, completed games, streaks, and best times. Clearing site data removes this information."],
      },
      {
        heading: "What we measure",
        paragraphs: ["Limited first-party events record actions such as starting, completing, or sharing a puzzle, plus how quickly and how steadily each page drew for you. They never include puzzle answers, free text, or a copied device identifier."],
      },
      {
        heading: "Cloudflare Web Analytics",
        paragraphs: ["The site uses Cloudflare Web Analytics for anonymous page-view and performance metrics. Cloudflare states that this product does not use client-side cookies for analytics and is designed not to track individual users across sites."],
      },
      {
        heading: "Google Analytics",
        paragraphs: ["The site uses Google Analytics (Google tag G-6PSRJZFVE1) to understand how people find and use the pages. Google may set cookies such as _ga on your device and receive standard usage data, including the pages you visit. Clearing site data or blocking the script stops this. We do not sell this information.", "If you are in the EEA, the UK, or Switzerland, analytics and advertising storage are denied by default and are never granted, so no _ga cookie is set and you are not recognised across visits. There is no consent banner because there is nothing here to consent to."],
      },
      {
        heading: "No accounts or data sales",
        paragraphs: ["There are no user accounts or data sales in this version of Nonogram Now. Puzzle progress stays in your browser. Google Analytics is used only for traffic measurement."],
      },
      {
        heading: "Questions about this policy",
        paragraphs: [`Write to ${SITE_EMAIL} with any question about what this site collects. This policy was last updated on 5 September 2026.`],
      },
    ],
  },
  {
    path: "/terms",
    title: "Terms of use — Nonogram Now",
    description: "The terms for using Nonogram Now: play and print the puzzles freely, personal and classroom use is welcome, bulk scraping and republishing are not.",
    eyebrow: "The short version",
    heading: "Terms of use",
    lede: "Play the puzzles, print them, share your results. Do not scrape the site or pass it off as your own.",
    relatedPaths: ["/", "/printable-nonograms", "/collection", "/how-to-play-nonogram"],
    sections: [
      {
        heading: "Using the site",
        paragraphs: ["Nonogram Now is free to use and needs no account. You may play any puzzle, print the sheets, and share your results and screenshots, including publicly."],
      },
      {
        heading: "Puzzles and artwork",
        paragraphs: ["The puzzle pictures, the site\u2019s text, and its code are the work of its author. You may print and copy the puzzle sheets for personal, classroom, or other non-commercial use, and we would rather you did. Republishing the puzzle library as your own, selling it, or using automated tools to bulk-download it is not permitted."],
      },
      {
        heading: "No guarantees",
        paragraphs: ["This is a side project provided as-is. It may be offline, change, or stop being updated without notice. Progress kept in your browser can be lost by clearing site data, by a browser update, or by a bug \u2014 treat a streak as a small pleasure rather than something to protect."],
      },
      {
        heading: "Changes to these terms",
        paragraphs: [`These terms may change as the site does. The current version is always the one on this page. They were last updated on 5 September 2026; questions go to ${SITE_EMAIL}.`],
      },
    ],
  },
];

export function seoPageForPath(pathname: string): SeoPage | undefined {
  return SEO_PAGES.find((page) => page.path === pathname);
}
